import { useState, useEffect } from "react"
import { Modal, Button, Spinner } from "react-bootstrap"
import { useDispatch, useSelector } from "react-redux"
import { useParams } from "react-router-dom"
import { getFollowUpAction, createFollowUpAction } from "../../../../../store/actions/FollowUpAction"
import { showErrorAlert, handleSuccess } from "../../../../../services/ErrorHandlingService"
import { formatDate } from "../../../../../utils/dateUtils"
import axiosInstance from "../../../../../services/AxiosInstance.js"

const LeadFollowUp = () => {
  const dispatch = useDispatch()
  const { id } = useParams()

  const leadDetails = useSelector((state) => state.leadState?.leadDetails)
  const lead_id = leadDetails?.lead_id || id

  const followUpState = useSelector((state) => state.followUps || {})
  const { followUps = [], isLoading = false, error = null } = followUpState

  const [showModal, setShowModal] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [currentEditId, setCurrentEditId] = useState(null)
  const [refreshTrigger, setRefreshTrigger] = useState(0)
  const [submitting, setSubmitting] = useState(false)
  const [completingId, setCompletingId] = useState(null)

  const [formData, setFormData] = useState({
    reason: "",
    next_action: "",
    next_follow_up_date: "",
    next_followup_time: "", // new field
  })

  useEffect(() => {
    if (lead_id) {
      dispatch(getFollowUpAction(lead_id))
    }
  }, [lead_id, dispatch, refreshTrigger])

  const resetForm = () => {
    setFormData({
      reason: "",
      next_action: "",
      next_follow_up_date: "",
      next_followup_time: "",
    })
  }

  const handleModalClose = () => {
    setShowModal(false)
    setIsEditMode(false)
    setCurrentEditId(null)
    resetForm()
  }

  const handleAddClick = () => {
    setIsEditMode(false)
    setCurrentEditId(null)
    resetForm()
    setShowModal(true)
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (submitting) return
    setSubmitting(true)

    if (!lead_id) {
      showErrorAlert("Lead ID not found. Please refresh and try again.", "error")
      setSubmitting(false)
      return
    }

    try {
      const result = await dispatch(createFollowUpAction(formData, lead_id))
      if (result && result.success) {
        handleModalClose()
        setRefreshTrigger((prev) => prev + 1)
        setTimeout(() => {
          dispatch(getFollowUpAction(lead_id))
        }, 500)
      }
    } catch (error) {
      console.error("Submit error:", error)
      showErrorAlert("Failed to save follow-up.", "error")
    } finally {
      setSubmitting(false)
    }
  }

  const handleCompleteFollowUp = async (followup_id) => {
    if (completingId === followup_id) return
    setCompletingId(followup_id)

    try {
      const apiFormData = new FormData()
      apiFormData.append("followup_id", followup_id)
      const response = await axiosInstance.post("/lead/followupcomplete", apiFormData)

      if (response?.data?.status === "success") {
        handleSuccess(response?.data?.message || "Follow-up completed")
        setRefreshTrigger((prev) => prev + 1)
      } else {
        throw new Error(response?.data?.message || "Failed to complete")
      }
    } catch (error) {
      console.error("Complete FollowUp Error:", error)
      showErrorAlert(
        error?.response?.data?.message || error.message || "Something went wrong"
      )
    } finally {
      setCompletingId(null)
    }
  }

  return (
    <div className="card shadow-none">
      <div className="card-body">
        <div className="d-flex justify-content-end align-items-center mb-3">
          <Button variant="outline-primary" size="sm" onClick={handleAddClick} disabled={!lead_id}>
            Add Follow-Up
          </Button>
        </div>

        {/* Follow-Up List */}
        {isLoading ? (
          <div className="text-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p>Loading follow-ups...</p>
          </div>
        ) : followUps && followUps.length > 0 ? (
          <div>
            {followUps.map((item, index) => (
              <div
                key={item.followup_id || index}
                className="mb-2 custom-timeline-panel timeline-panel bgl-dark p-3 rounded border d-flex justify-content-between align-items-center"
              >
                <div>
                  <p className="mb-0">
                    <strong>Reason:</strong> {item.followup_reason || item.reason || "N/A"}
                  </p>
                  <p className="mb-0">
                    <strong>Next Action:</strong> {item.next_followup_action || item.next_action || "N/A"}
                  </p>
                  <p className="mb-0">
                    <strong>Follow-Up Date:</strong> {formatDate(item.next_followup_date) || "-"}
                  </p>
                  {/* Display reminder time if exists */}
                  {item.next_followup_time && (
                    <p className="mb-0">
                      <strong>Reminder Time:</strong> {item.next_followup_time}
                    </p>
                  )}
                </div>

                {item.followup_status !== "Complete" && (
                  <Button
                    variant="outline-success"
                    size="sm"
                    onClick={() => handleCompleteFollowUp(item.followup_id)}
                    disabled={completingId === item.followup_id}
                  >
                    {completingId === item.followup_id ? (
                      <>
                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                        Completing...
                      </>
                    ) : (
                      "Complete"
                    )}
                  </Button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center text-muted">No FollowUp found.</div>
        )}

        {/* Follow-Up Modal */}
        <Modal show={showModal} onHide={handleModalClose}>
          <Modal.Header closeButton>
            <Modal.Title>{isEditMode ? "Edit Follow-Up" : "Add Follow-Up"}</Modal.Title>
          </Modal.Header>
          <form onSubmit={handleSubmit}>
            <Modal.Body>
              <div className="mb-3">
                <label className="form-label">
                  Reason for Follow-Up <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="reason"
                  value={formData.reason}
                  onChange={handleChange}
                  placeholder="Enter reason for follow-up"
                  required
                />
              </div>
              <div className="mb-3">
                <label className="form-label">
                  Next Action <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="next_action"
                  value={formData.next_action}
                  onChange={handleChange}
                  placeholder="Enter next action"
                  required
                />
              </div>
              <div className="mb-3">
                <label className="form-label">
                  Next Follow-Up Date <span className="text-danger">*</span>
                </label>
                <input
                  type="date"
                  className="form-control"
                  name="next_follow_up_date"
                  value={formData.next_follow_up_date}
                  onChange={handleChange}
                  min={new Date().toISOString().split("T")[0]}
                  required
                />
              </div>

              {/* Reminder Time Field with Clock Icon */}
              <div className="mb-3">
                <label htmlFor="reminderTime" className="form-label">
                Next Follow-Up Time
                </label>
                <div className="position-relative">
                  <input
                    type="time"
                    className="form-control"
                    id="reminderTime"
                    name="next_followup_time"
                    value={formData.next_followup_time}
                    onChange={handleChange}
                    autoComplete="off"
                    style={{
                      paddingRight: "40px",
                      WebkitAppearance: "none",
                      MozAppearance: "textfield",
                    }}
                  />
                  <i
                    className="fa fa-clock position-absolute"
                    style={{
                      right: "15px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      pointerEvents: "none",
                      fontSize: "18px",
                      color: "#888",
                      zIndex: 2,
                    }}
                  ></i>
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleModalClose}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={submitting}>
                {submitting ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {isEditMode ? "Updating..." : "Saving..."}
                  </>
                ) : (
                  isEditMode ? "Update" : "Save"
                )}
              </Button>
            </Modal.Footer>
          </form>
        </Modal>
      </div>
    </div>
  )
}

export default LeadFollowUp